<template>
  <!-- Rebuilt on the KarroFin components. Same endpoint, same params, same
       Vuex filter module -- only the presentation moved. -->
  <div class="kf-page">
    <!-- The same underline tabs the Dashboard draws, rather than the KPI-card
         row this used to be. The buckets ARE the product-type filter; "All" is
         the first of them, so it doubles as the cleared state and ProductSwitch's
         no-toggle rule costs nothing here.

         ProductSwitch keys on the label, this screen's API keys on `param`
         ("all" / "Personal Loan" / "Business Loan"), so the two computeds below
         map between them. When an admin has pinned this user's product type the
         row narrows to their tab rather than disappearing -- the count is theirs
         to see.

         The per-bucket total amount the cards used to carry moves to the row's
         action slot, for the selected bucket only: a tab has room for a count,
         not for a currency figure, and dropping the number outright would take a
         disbursed total off a disbursement screen. -->
    <ProductSwitch
      v-if="buckets.length"
      :value="activeProduct"
      :counts="productCounts"
      @change="selectProduct"
    >
      <template #action>
        <span class="kf-bucket-total">
          {{ filters.match_state === "unmatched" ? "Lender-reported amount" : "Total amount" }}:
          {{ fmt.currencyShort(activeBucketAmount) }}
        </span>
      </template>
    </ProductSwitch>

    <!-- All / Matched / Unmatched, the same control Leads has. Matched rows
         are the disbursed offers; unmatched rows are what lenders reported as
         disbursed that reached no offer -- MIS-<id> report leads and CC<id>
         tracked clicks. Their money is reported, not recorded: "Total amount"
         counts it under All and Unmatched (the server's tab amount follows
         the rows shown) and leaves it out under Matched. Shown on its own
         here too; clicking it shows those rows. -->
    <div class="kf-match-row">
      <v-select
        v-model="filters.match_state"
        :items="matchOptions"
        class="kf-match-row__select"
        dense
        outlined
        hide-details
        @change="reload()"
      />
      <button
        type="button"
        class="kf-match-row__amount"
        title="Show the unmatched rows"
        @click="showUnmatched"
      >
        Lender-reported disbursals, unmatched:
        <strong>{{ reportedAmountText }}</strong>
      </button>
    </div>

    <!-- Seven filters that used to be folded inside a collapsed accordion. The
         five UTM ones were each ~40 lines of hand-rolled "Select All" markup
         with a tri-state icon; MultiSelect ships that behaviour, so they are
         one line apiece now. -->
    <FilterBar
      :search.sync="search_query"
      :show-date="false"
      :advanced-count="activeChips.length"
      :rest-count="restCount"
      search-placeholder="Search by name, mobile or ID"
      @change="reload()"
      @reset="resetFilters"
    >
      <!--
        One ordered list rather than a fixed visible row plus an `advanced`
        slot -- see the note on Offer Management. A hidden filter drops out and
        the next takes its track instead of leaving an empty cell.
      -->
      <template v-for="f in filterLayout">
        <div
          :key="f.key"
          class="kf-field"
          :class="{ 'kf-field--rest': f.rest }"
        >
          <span class="kf-field__label">{{ f.label }}</span>

          <DateField
            v-if="f.key === 'disbursal_range'"
            v-model="filters.date_range"
            range
            :max="today"
            @change="reload()"
          />
          <!-- The Leads screen's Applied On, so one range means the same on both -->
          <DateField
            v-else-if="f.key === 'applied_range'"
            v-model="filters.applied_range"
            range
            :max="today"
            @change="reload()"
          />
          <v-autocomplete
            v-else-if="f.key === 'product'"
            v-model="filters.product"
            :items="product_list"
            :search-input.sync="product_search"
            item-text="title"
            item-value="id"
            placeholder="Any product"
            dense
            outlined
            single-line
            clearable
            hide-details
            hide-selected
            @change="reload()"
            @focus="getProductList()"
          ></v-autocomplete>
          <MultiSelect
            v-else-if="f.key === 'utm_source'"
            v-model="filters.utm_source"
            :items="utm_list.utm_sources"
            @change="reload()"
          />
          <MultiSelect
            v-else-if="f.key === 'utm_medium'"
            v-model="filters.utm_medium"
            :items="utm_list.utm_mediums"
            @change="reload()"
          />
          <MultiSelect
            v-else-if="f.key === 'utm_content'"
            v-model="filters.utm_content"
            :items="utm_list.utm_contents"
            @change="reload()"
          />
          <MultiSelect
            v-else-if="f.key === 'utm_campaign'"
            v-model="filters.utm_campaign"
            :items="utm_list.utm_campaigns"
            @change="reload()"
          />
          <MultiSelect
            v-else-if="f.key === 'utm_term'"
            v-model="filters.utm_term"
            :items="utm_list.utm_terms"
            @change="reload()"
          />
          <!-- Bound to `utm_platform`, the key the store declares and the
               request sends. It used to write `utm_placement`, which is not a
               field on the store's state object -- so the write landed as a
               non-reactive property, the request sent an always-empty
               `utm_platform`, and Reset could not clear the control. -->
          <MultiSelect
            v-else-if="f.key === 'utm_platform'"
            v-model="filters.utm_platform"
            :items="utm_list.utm_platforms"
            @change="reload()"
          />
        </div>
      </template>

      <!-- What is applied, in words. Five filters sit behind "More filters" on
           this screen, so with the panel shut a bare count was the only sign
           anything was narrowing the list -- and you could not tell which. -->
      <template #chips>
        <v-chip
          v-for="c in activeChips"
          :key="c.key"
          small
          close
          class="kf-fchip"
          @click:close="clearFilter(c.key)"
        >
          <strong>{{ c.label }}:</strong>&nbsp;{{ c.value }}
        </v-chip>
      </template>
    </FilterBar>

    <DataTable
      title="Disbursements"
      :headers="headers"
      :items="disbursement_list"
      :loading="table_loading"
      :page="filters.page_number"
      :total-pages="total_page_count"
      :total-count="total_count"
      :page-length="$keys.PAGE_LENGTH"
      empty-title="No disbursements match these filters"
      empty-message="Try another bucket above, a wider date range, or clearing a UTM filter."
      empty-icon="banknote"
      :error="error"
      @page-change="onPage"
      @retry="getDisbursementList()"
    >
      <template #actions>
        <DownloadReport :action="downloadReport" />
      </template>

      <!-- An offer shows its id; an unmatched row its own reference, tagged,
           with why it is not matched yet. -->
      <template #[`item.id`]="{ item }">
        <div class="kf-stack">
          <div class="kf-truncate" :title="item.id">{{ item.id }}</div>
          <div v-if="isUnmatched(item)" class="kf-stack__sub kf-truncate" :title="item.unmatched_reason_label">
            <span class="kf-tag kf-tag--unmatched">Unmatched</span>
          </div>
        </div>
      </template>

      <template #[`item.name`]="{ item }">
        <div v-if="isUnmatched(item)" class="kf-truncate kf-muted" :title="item.unmatched_reason_label">
          {{ fmt.orDash(item.unmatched_reason_label) }}
        </div>
        <template v-else>{{ fmt.orDash(item.name) }}</template>
      </template>

      <template #[`item.title`]="{ item }">
        <div class="kf-truncate" :title="item.title">
          {{ fmt.orDash(item.title) }}
        </div>
      </template>

      <template #[`item.mobile`]="{ item }">
        <div class="kf-stack__main">{{ fmt.mobile(item.mobile) }}</div>
        <div class="kf-stack__sub" :title="item.email">
          {{ fmt.orDash(item.email) }}
        </div>
      </template>

      <!-- As on Leads: the date, with the time of day on the tooltip -->
      <template #[`item.applied`]="{ item }">
        <span :title="item.applied">{{ fmt.date(item.applied) }}</span>
      </template>

      <template #[`item.disbursal_date`]="{ item }">
        {{ fmt.date(item.disbursal_date) }}
      </template>

      <!-- Tabular figures so the digits line up column-wise down the page, the
           way a ledger is read. -->
      <template #[`item.disbursal_amount`]="{ item }">
        <span class="kf-amount">{{ fmt.currency(item.disbursal_amount) }}</span>
      </template>

      <!-- Falsy, not `=== null`: the column is free text, so "" arrives as often
           as null and used to render as a blank cell rather than a dash. -->
      <template #[`item.remark`]="{ item }">
        <div class="kf-truncate" :title="item.remark">
          {{ item.remark || "-" }}
        </div>
      </template>

      <!-- The rows are the raw response here -- no mapper -- so the utm_*
           fields sit directly on the item, same as Leads. -->
      <template #[`item.utm`]="{ item }">
        <span v-if="isUnmatched(item)">-</span>
        <a v-else class="kf-utm" @click.stop="ViewUtm(item)">
          {{ item.utm_source || "View" }}
        </a>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-tooltip v-if="item.can_match && canMatch" bottom>
          <template #activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="openMatch(item)">
              <LucideIcon name="link" :size="16" />
            </v-btn>
          </template>
          <span>Match to an offer</span>
        </v-tooltip>
      </template>
    </DataTable>

    <!-- The legacy screens' dialog, unchanged: no props, reads the six utm_*
         keys off whatever object openDialog is handed. -->
    <ViewUTMDetail ref="UtmDialog" />

    <ReportLeadMatchDialog v-model="matchOpen" :row="matchRow" @matched="onMatched" />
  </div>
</template>
<script>
import {
  getDefaultSignupDateRangeIso,
} from "@/utils/defaultSignupDateRange";
import { mapGetters } from "vuex";
import debounce from "lodash/debounce";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import DateField from "@/components/karrofin/DateField.vue";
import MultiSelect from "@/components/karrofin/MultiSelect.vue";
import ProductSwitch from "@/components/karrofin/ProductSwitch.vue";
import DownloadReport from "@/components/karrofin/DownloadReport.vue";
import ReportLeadMatchDialog from "@/components/karrofin/ReportLeadMatchDialog.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import { UTM_KEYS, utmParams } from "@/app-js/utm-filters";

/* Buckets the API still sends but the panel no longer offers, matched on the
   `param` the backend filters by (keys.CREDIT_CARD). */
const RETIRED_BUCKETS = ["Credit Card"];

/* Every filter this screen offers, in display order -- see the note on
   Offer Management. `key` is the lock's parameter name.

   Every one is always drawn, including a UTM filter whose dropdown came back
   empty. Hiding those was tried and pulled back out: the row is meant to be four
   filters wide with the rest behind "More filters", and on an environment with
   no UTM data the hiding collapsed it to three and took the button away
   entirely. A layout that changes shape with the data is harder to work with
   than an empty select. */
const FILTER_ORDER = [
  { key: "disbursal_range", label: "Disbursement date" },
  { key: "applied_range", label: "Applied on" },
  { key: "product", label: "Product" },
  { key: "utm_source", label: "Partner ID (utm_source)" },
  { key: "utm_medium", label: "Publisher (utm_medium)" },
  { key: "utm_content", label: "Source (utm_content)" },
  { key: "utm_campaign", label: "UTM campaign" },
  { key: "utm_term", label: "UTM term" },
  { key: "utm_platform", label: "UTM platform" },
];

/* Chip labels for the filters that carry a value worth naming back. The UTM
   ones share a shape, so they are built rather than listed. */

const UNKNOWN = "—";

/* Three beside Search -- this screen has no date control in the bar, so the row
   that always shows is Search, Disbursement date, Applied on, Product. */
const PRIMARY_SLOTS = 3;

export default {
  components: {
    FilterBar,
    DataTable,
    DateField,
    MultiSelect,
    ProductSwitch,
    DownloadReport,
    ReportLeadMatchDialog,
    ViewUTMDetail: () => import("@/components/las/ViewUTMDetail.vue"),
  },
  data() {
    return {
      debouncedLeadSearch: null,
      product_search: "",
      today: new Date().toISOString().substr(0, 10),
      tabs_title: [],
      table_loading: false,
      error: null,
      fmt,
      total_page_count: 1,
      total_count: null,
      disbursement_list: [],
      match_counts: {},
      unresolved_reported: {},
      matchOpen: false,
      matchRow: null,
      product_list: [],
      /* Owned by the page now rather than read from the shared navbar search. */
      search_query: "",
      utm_list: {
        utm_campaigns: [],
        utm_sources: [],
        utm_mediums: [],
        utm_contents: [],
        utm_terms: [],
        utm_platforms: [],
      },
      headers: [
        { text: "ID", value: "id", align: "start", width: 80 },
        { text: "Name", value: "name", align: "start", width: 140 },
        { text: "Product", value: "title", align: "start", width: 150 },
        { text: "Contact", value: "mobile", align: "start", width: 190 },
        /* When the borrower applied -- the Leads screen's Applied On, so a
           disbursal can be traced back to its lead. Name, Product and Remark
           gave up the width. */
        { text: "Applied On", value: "applied", align: "start", width: 120 },
        {
          text: "Disbursal Date",
          value: "disbursal_date",
          align: "start",
          width: 120,
        },
        /* End-aligned, like every other money column in the panel: a column of
           right-aligned figures lines its digits up by place value, so ₹50,000
           and ₹1,50,000 are visibly different lengths. */
        {
          text: "Disbursal Amount",
          value: "disbursal_amount",
          align: "end",
          width: 150,
        },
        { text: "Remark", value: "remark", align: "start", width: 120 },
        /* Where this disbursal came from. The screen has filtered on all six
           UTM fields since it was built but could never show them, so an
           operator could narrow the ledger to a campaign and then not see
           which row belonged to it. The cell shows utm_source so the column
           answers something on its own; the rest is behind the popup. */
        { text: "UTM", value: "utm", align: "start", width: 96 },
        /* Match, on unmatched MIS-<id> rows only */
        { text: "", value: "actions", align: "end", width: 56 },
      ],
    };
  },
  methods: {
    /* Row -> UTM popup. The rows are raw here, so the row IS the payload. */
    ViewUtm(item) {
      this.$refs.UtmDialog.openDialog(item);
    },
    isUnmatched(item) {
      return Boolean(item.row_type && item.row_type !== "lead");
    },
    showUnmatched() {
      if (this.filters.match_state === "unmatched") return;
      this.filters.match_state = "unmatched";
      this.reload();
    },
    openMatch(item) {
      this.matchRow = item;
      this.matchOpen = true;
    },
    onMatched(result) {
      this.showSnakeBar(
        "success",
        result && result.status_applied === false
          ? `${result.reference} matched; the offer already had a newer status, so it was kept`
          : `${(result && result.reference) || "Row"} matched to offer ${result && result.offer_id}`
      );
      this.getDisbursementList();
    },
    /* A filter changed. Page 1, because the row that was on page 6 under the old
       filter is not the row that is there under the new one -- and the server's
       Paginator clamps an out-of-range page to the last one, so the table
       silently showed the last page's rows while the pager still read "6 of 2".
       Same fix, and the same reason, as Leads. */
    reload() {
      this.filters.page_number = 1;
      this.getDisbursementList();
    },
    /* ProductSwitch hands back the label it drew, so translate to the `param`
       the API filters on. An unknown label would otherwise blank the filter. */
    selectProduct(label) {
      const bucket = this.buckets.find((b) => b.title === label);
      if (!bucket) return;
      this.filters.product_type = bucket.param;
      this.reload();
    },
    onPage(page) {
      this.filters.page_number = page;
      this.getDisbursementList();
      /* Paging from the bottom of a long list otherwise leaves you scrolled past
         the new rows. Both sibling screens do this. */
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    /* Everything the bar can express, including the two the old version left
       behind: the page you are on and the bucket you picked. Resetting from page
       4 of Business Loan used to leave you on page 4 of Business Loan. */
    resetFilters() {
      this.filters.date_range = getDefaultSignupDateRangeIso();
      this.filters.applied_range = [];
      this.filters.product = "";
      this.filters.product_type = "";
      this.filters.match_state = "all";
      UTM_KEYS.forEach((key) => (this.filters[key] = []));
      this.search_query = "";
      this.product_search = "";
      this.reload();
    },
    /* One filter's × on the chips row. Arrays back to empty, scalars to "" --
       the shapes the request builder and the controls both expect. */
    clearFilter(key) {
      if (key === "date_range") this.filters.date_range = [];
      else if (Array.isArray(this.filters[key])) this.filters[key] = [];
      else this.filters[key] = "";
      this.reload();
    },
    getUtmList() {
      const self = this;

      const successHandler = (response) => {
        self.utm_list = response.data.result;
      };

      const finallyHandler = () => {};

      self.request_GET(
        self,
        self.$urls.UTM_LIST,
        {},
        successHandler,
        null,
        null,
        finallyHandler
      );
    },
    getProductList() {
      let self = this;

      const successHandler = (response) => {
        self.product_list = response.data.result;
      };

      const failureHandler = (error) => {
        console.error("Product list fetch failed", error);
      };

      const finallyHandler = () => {};

      const params = {};

      if (self.product_search && self.product_search.trim() !== "") {
        params.search = self.product_search;
      }

      params.filter = true;

      self.request_GET(
        self,
        self.$urls.GET_PRODUCT_LIST,
        params,
        successHandler,
        null,
        failureHandler,
        finallyHandler
      );
    },

    /* The filters, as the request wants them. Shared with the export so the file
       holds exactly the rows on screen; `paged` is false there, since the server
       exports the whole filtered set rather than one page. */
    listParams(paged = true) {
      const f = this.filters;
      const params = {
        product: f.product,
        product_type: f.product_type,
        disbursal_from_date: f.date_range[0],
        disbursal_to_date: f.date_range[1],
        // Applied On, under the names the Leads screen sends it as
        from_date: (f.applied_range || [])[0],
        to_date: (f.applied_range || [])[1],
        ...utmParams(f),
        search_query: this.search_query,
        match_state: f.match_state || "all",
      };
      if (paged) {
        params.page_number = f.page_number;
        params.page_length = this.$keys.PAGE_LENGTH;
      }
      return params;
    },
    getDisbursementList() {
      let self = this;
      self.table_loading = true;
      self.error = null;
      const successHandler = (response) => {
        /* request_GET routes a `success: false` body here too, where `result` is
           null -- reading .allcount off it threw and left the table mid-update. */
        const result = (response && response.data && response.data.result) || null;
        if (!result) {
          self.error = "Could not load disbursements.";
          return;
        }
        self.tabs_title = result.allcount || [];
        self.disbursement_list = result.data || [];
        self.total_page_count = result.total_page_count;
        self.total_count =
          result.total_count !== undefined ? result.total_count : null;
        self.match_counts = result.match_counts || {};
        self.unresolved_reported = result.unresolved_reported || {};
      };
      /* Was an empty stub, so a failed load kept the previous rows on screen with
         nothing to say they were stale. DataTable renders ErrorState with a Retry
         from this. */
      const failureHandler = () => {
        self.error = "Could not load disbursements.";
      };
      const finallyHandler = () => {
        self.table_loading = false;
      };
      self.request_GET(
        self,
        self.$urls.GET_DISBURSEMENT_LIST,
        self.listParams(),
        successHandler,
        null,
        failureHandler,
        finallyHandler
      );
    },
    /* Handed to DownloadReport, which wants a promise and owns the busy state. */
    downloadReport() {
      return api.exportDisbursements(this.listParams(false));
    },
  },
  watch: {
    /* Fetches the PRODUCT options, not the list. It deliberately does not touch
       page_number: it used to reset it without reloading, which left the pager
       saying 1 while the table still showed page N. */
    product_search(val) {
      if (!val || val.length < 2) return;
      this.debouncedLeadSearch();
    },
  },
  computed: {
    /** Ops may match a lender-reported row to an offer ("MIS Match" grant). */
    canMatch() {
      return this.getPermission("MIS Match");
    },
    /* The dropdown's three choices with their counts for the selected tab,
       once the first reply is in; the plain words before that. */
    matchOptions() {
      const counts = this.match_counts || {};
      const counted = typeof counts.all === "number";
      const option = (text, value) => ({
        text: counted ? `${text} (${fmt.number(counts[value] || 0)})` : text,
        value,
      });
      return [
        option("All disbursals", "all"),
        option("Matched", "matched"),
        option("Unmatched", "unmatched"),
      ];
    },
    reportedAmountText() {
      const counts = this.match_counts || {};
      if (typeof counts.all !== "number") return UNKNOWN;
      return fmt.currency((this.unresolved_reported || {}).amount || 0);
    },
    /** Filters to draw, in order, each marked for the row or the panel. */
    filterLayout() {
      const shown = FILTER_ORDER.filter(
        (f) => !this.filterLocked("disbursement", f.key)
      );
      return shown.map((f, i) => ({ ...f, rest: i >= PRIMARY_SLOTS }));
    },
    restCount() {
      return this.filterLayout.filter((f) => f.rest).length;
    },
    /* The filter object is raw Vuex state, written straight through v-model --
       which is what makes these filters survive leaving the screen. Kept. */
    ...mapGetters({
      filters: "disbursement/getFilters",
    }),
    /* The API still returns a Credit Card bucket, but the product was retired
       (its routes are already commented out of the router), so the tile is
       dropped here rather than by changing a shared endpoint other screens
       also read. Filtering the view, not the response, keeps its count out of
       sight without pretending the data does not exist. */
    buckets() {
      const live = this.tabs_title.filter(
        (b) => !RETIRED_BUCKETS.includes(String(b.param))
      );
      return this.allowedSegments(
        "disbursement", "product_type", live, "param");
    },
    /* The tabs, in ProductSwitch's shape. It draws whatever it is handed, so the
       retired-bucket and product-lock filtering in `buckets` carries straight
       through and no tab needs hiding here. */
    productCounts() {
      return this.buckets.map((b) => ({
        label: b.title,
        count: b.count || 0,
      }));
    },
    /* The selected bucket, resolved to its label for ProductSwitch.

       On a fresh visit product_type is "", which matches no bucket; the table is
       unfiltered then, which is exactly what the first bucket ("All") stands for,
       so it leads. The KPI-card row this replaced lit its first tile on the same
       reasoning, as did the v-tabs before it. */
    activeBucket() {
      const picked = this.buckets.find(
        (b) => b.param === this.filters.product_type
      );
      return picked || this.buckets[0] || null;
    },
    activeProduct() {
      return this.activeBucket ? this.activeBucket.title : "";
    },
    activeBucketAmount() {
      return this.activeBucket ? this.activeBucket.amount : 0;
    },
    /* Every applied filter, as {key, label, value} -- the chips row below the bar
       and the "More filters" badge both count off this.

       It used to be a UTM-only count that also read `utm_placement`, a key the
       store does not declare. So the badge ignored the date range and the product
       entirely (set a date, see nothing applied) and the one UTM key it did read
       was the broken one. */
    activeChips() {
      const chips = [];
      const f = this.filters;
      if (f.date_range && f.date_range.length === 2) {
        chips.push({
          key: "date_range",
          label: "Disbursement date",
          value: fmt.dateRangeShort(f.date_range[0], f.date_range[1]),
        });
      }
      if (f.applied_range && f.applied_range.length === 2) {
        chips.push({
          key: "applied_range",
          label: "Applied on",
          value: fmt.dateRangeShort(f.applied_range[0], f.applied_range[1]),
        });
      }
      if (f.product) {
        const match = this.product_list.find((p) => p.id === f.product);
        chips.push({
          key: "product",
          label: "Product",
          value: match ? match.title : String(f.product),
        });
      }
      UTM_KEYS.forEach((key) => {
        const picked = f[key];
        if (!Array.isArray(picked) || !picked.length) return;
        const entry = FILTER_ORDER.find((x) => x.key === key);
        chips.push({
          key,
          label: entry ? entry.label : key,
          value: picked.length === 1 ? picked[0] : picked.length + " selected",
        });
      });
      return chips;
    },
  },
  created() {
    /* The filter object survives leaving the screen, so it can still be holding
       a bucket that no longer has a tile. Left alone, the table would come back
       filtered with nothing on screen to clear it. */
    if (RETIRED_BUCKETS.includes(String(this.filters.product_type))) {
      this.filters.product_type = "";
    }
    /* Same reasoning for the page: it survives too, so coming back to the screen
       re-requested page 7 of a list you are seeing for the first time. */
    this.filters.page_number = 1;
    this.debouncedLeadSearch = debounce(() => {
      this.getProductList();
    }, 500);
  },
  mounted() {
    this.getProductList();
    this.getDisbursementList();
    this.getUtmList();
  },
  beforeDestroy() {
    this.debouncedLeadSearch.cancel();
  },
};
</script>

<style scoped>
/* The disbursed total for the selected tab, sitting in ProductSwitch's action
   slot. Muted and regular-weight so it reads as a figure attached to the row
   rather than a fourth tab competing with the three. */
.kf-bucket-total {
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: var(--kf-text-muted);
  white-space: nowrap;
}
/* Two values in one cell: the number reads first, the address under it a tier
   quieter -- same shape the KarroFin user cells use. */
.kf-stack__main {
  font-size: var(--kf-fs-body);
  color: #000;
}
.kf-stack__sub {
  font-size: var(--kf-fs-meta);
  color: var(--kf-text-muted);
  font-weight: 400;
  /* A sub-line that wraps makes its row twice the height of the ones around it,
     and email addresses are long enough to do it often. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* One-line cell for the free-text columns; the full value is in the title. */
.kf-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The All / Matched / Unmatched dropdown and the reported amount beside it,
   as on Leads. Wraps under the select on a phone. */
.kf-match-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-bottom: 12px;
}
.kf-match-row__select {
  flex: 0 1 260px;
  max-width: 260px;
}
.kf-match-row__amount {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  text-align: left;
}
.kf-match-row__amount:hover {
  text-decoration: underline;
}
.kf-stack {
  min-width: 0;
  padding: 2px 0;
}
.kf-muted {
  color: var(--kf-text-muted);
}

/* Marks a row that is a lender's report with no offer behind it yet. */
.kf-tag {
  display: inline-block;
  padding: 0 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: var(--kf-fs-label);
}
.kf-tag--unmatched {
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fcd34d;
}

/* Lining figures of equal width, so the rupee columns stack digit over digit. */
.kf-amount {
  font-variant-numeric: tabular-nums;
}

/* The UTM cell is the click target for the details popup, so it reads as a
   link rather than as plain text sitting in a column. */
.kf-utm {
  color: #00695c;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.kf-utm:hover {
  text-decoration: underline;
}
</style>
